/* eslint-disable max-len */
import { darken } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { Component } from '../../../concepts/objects';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { CommentCountAttr } from '../../packages/annotations/CommentAssociations';
import { Tag } from '../../packages/annotations/Tag';
import { TagTextSettings } from '../../packages/annotations/TagLayout';
import { VoteCountAttr } from '../../packages/voting/Vote';
import { TextSettings } from '../entities/TextConventions';
import { useComponentObserver } from './ComponentObserver';
import { Pose } from './Pose';

export type AnnotationsPosition = 'bottomRight' | 'right';

export const RenderAnnotations: LFC<{ object: Component, position?: AnnotationsPosition }> = props => <>
    {/* <AnnotationMount position={props.position}>
        <VoteCountAnnotation object={props.object} />
    </AnnotationMount> */}
</>;

export const RenderComments: LFC<{ object: Component }> = ({ object }) => {
    const area = (object as any)?.layout?.tags?.comments as Rect;
    if (!area?.width || !area?.height) return null;
    const { interaction } = Pose.get(object);
    const active = (interaction?.type === 'invoke' && interaction?.hint === 'comments');
    return <>
        <AnnotationFrame active={active} color={Colors.mystery} style={area.toCSS()}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M3.43 2.524A41.29 41.29 0 0110 2c2.236 0 4.43.18 6.57.524 1.437.231 2.43 1.49 2.43 2.902v5.148c0 1.413-.993 2.67-2.43 2.902a41.202 41.202 0 01-5.183.501.78.78 0 00-.528.224l-3.579 3.58A.75.75 0 016 17.25v-3.443a41.033 41.033 0 01-2.57-.33C1.993 13.244 1 11.986 1 10.573V5.426c0-1.413.993-2.67 2.43-2.902z" clipRule="evenodd" />
            </svg>
            <span>
                {CommentCountAttr.get(object)}
            </span>
        </AnnotationFrame>
    </>;
};

export const RenderVotes: LFC<{ object: Component, color?: string }> = ({ object, color }) => {
    const area = (object as any)?.layout?.tags?.votes as Rect;
    if (!area?.width || !area?.height) return null;
    const { interaction } = Pose.get(object);
    const active = (interaction?.type === 'invoke' && interaction?.hint === 'votes');
    return <>
        <AnnotationFrame active={active} color={color ?? Colors.mystery} style={area.toCSS()}>
            <span>
                +{VoteCountAttr.get(object)}
            </span>
        </AnnotationFrame>
    </>;
};

export const RenderAnnotation: LFC<{ rect: Rect, active?: boolean }> = ({ rect, active, children }) => {
    if (!rect.width || !rect.height) return null;
    return <AnnotationFrame active={active} color={Colors.negative} style={rect.toCSS()}>
        {children}
    </AnnotationFrame>;
};

export const AnnotationFrame = styled.div<{ active?: boolean, color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    position: absolute;
    color: white;
    font-family: "Rubik";
    font-size: 9px;
    font-weight: 450;
    line-height: 1em;
    text-transform: uppercase;
    background: ${p => p.active ? darken(.2, p.color) : p.color};
    border-radius: 2px;

    .emoji {
        font-size: 8px;
    }

    svg {
        width: 12px;
        height: 12px;
    }
`;

export const RenderTags: LFC<{ object: Component, textSettings?: Partial<TextSettings> }> = ({ object, textSettings }) => {
    return <>
        {object.polarize(Tag).map(tag =>
            <RenderTag key={tag.id} {...{ object, tag, textSettings }} />
        )}
    </>;
};

export const RenderTag: LFC<{ object: Component, tag: Tag, textSettings?: Partial<TextSettings> }> = ({ object, tag, textSettings }) => {
    useComponentObserver(tag);
    if (!object.alive || !tag.alive) return null;
    const tl = (object as any)?.layout?.tags ?? {};
    const area = tl[tag.id] as Rect;
    if (!area?.width || !area?.height) return null;
    const { interaction } = Pose.get(object);
    const active = (interaction?.type === 'invoke' && interaction?.hint === tag.id);
    const styling = {
        ...area.toCSS(),
        ...TextSettings.toCSS({ ...TagTextSettings, ...(textSettings ?? {}) }),
        lineHeight: '1em',
        margin: 0,
        color: Colors.contrastTextColorFor(tag.color),
    };

    return <>
        <AnnotationFrame active={active} color={tag.color} style={styling}>
            <span>{tag.text}</span>
        </AnnotationFrame>
    </>;
};

// const RenderTagText = ({ value }: { value: string }) => {
//     if (!value) return null;
//     return <span>{value}</span>;
// }

// const RenderTagSymbol = ({ symbol }: { symbol: string }) => {
//     if (!symbol) return null;
//     const emoji = EmojiStore.find(symbol)
//     return !!emoji ? <Emoji idOrCode={symbol} /> : <span>{symbol}</span>;
// }
